'use client';

import { useState } from 'react';
import { Save, Upload, Check, Building2, Globe, Mail, Phone, MapPin } from 'lucide-react';
import { mockCompany } from '@/lib/mock-data';

export default function AdminSettingsPage() {
  const [saved, setSaved] = useState(false);

  const [company, setCompany] = useState({
    name: mockCompany.name || 'Bestattungsunternehmen',
    street: 'Musterstraße 123',
    zipCode: '40210',
    city: 'Düsseldorf',
    phone: mockCompany.phone || '+49 211 1234567',
    email: mockCompany.email || 'info@bestattungen-example.de',
    website: mockCompany.websiteUrl || '',
  });

  const [features, setFeatures] = useState({
    enableCondolences: true,
    enableCandles: true,
    enableMemories: true,
    enableGallery: true,
    moderateCondolences: false,
    moderateMemories: true,
    allowAnonymous: false,
    emailNotifications: true,
  });

  const [privacy, setPrivacy] = useState({
    defaultVisibility: 'public',
    showInSearch: true,
    allowSharing: true,
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Einstellungen</h1>
          <p className="text-gray-500 mt-1">Verwalten Sie die allgemeinen Portaleinstellungen</p>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-4 py-2.5 bg-memorial-primary text-white rounded-lg hover:bg-memorial-primary/90 transition-colors"
        >
          {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          {saved ? 'Gespeichert' : 'Änderungen speichern'}
        </button>
      </div>

      {/* Company information */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Building2 className="w-5 h-5 text-memorial-primary" />
          <h2 className="text-lg font-medium text-gray-900">Unternehmensdaten</h2>
        </div>

        {/* Logo upload */}
        <div className="flex items-start gap-6 mb-6 pb-6 border-b border-gray-200">
          <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-10 h-10 text-gray-400" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900 mb-1">Firmenlogo</h3>
            <p className="text-sm text-gray-500 mb-3">
              Wird im Portal und auf Gedenkseiten angezeigt. Empfohlen: 200x200px, PNG oder SVG.
            </p>
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm">
              <Upload className="w-4 h-4" />
              Logo hochladen
            </button>
          </div>
        </div>

        {/* Form fields */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Firmenname</label>
            <input
              type="text"
              value={company.name}
              onChange={(e) => setCompany(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Straße</label>
            <input
              type="text"
              value={company.street}
              onChange={(e) => setCompany(prev => ({ ...prev, street: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PLZ</label>
            <input
              type="text"
              value={company.zipCode}
              onChange={(e) => setCompany(prev => ({ ...prev, zipCode: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stadt</label>
            <input
              type="text"
              value={company.city}
              onChange={(e) => setCompany(prev => ({ ...prev, city: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={company.phone}
                onChange={(e) => setCompany(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={company.email}
                onChange={(e) => setCompany(prev => ({ ...prev, email: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={company.website}
                onChange={(e) => setCompany(prev => ({ ...prev, website: e.target.value }))}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Feature settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Funktionen</h2>

        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">Aktivierte Features</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { key: 'enableCondolences', label: 'Kondolenzbuch' },
                { key: 'enableCandles', label: 'Kerzenwand' },
                { key: 'enableMemories', label: 'Erinnerungswand' },
                { key: 'enableGallery', label: 'Bildergalerie' },
              ].map((feature) => (
                <label key={feature.key} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={features[feature.key as keyof typeof features] as boolean}
                    onChange={(e) => setFeatures(prev => ({ ...prev, [feature.key]: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-300 text-memorial-primary focus:ring-memorial-primary"
                  />
                  <span className="text-sm text-gray-700">{feature.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Moderation</h3>
            <div className="space-y-3">
              {[
                { key: 'moderateCondolences', label: 'Kondolenzen vor Veröffentlichung prüfen' },
                { key: 'moderateMemories', label: 'Erinnerungen vor Veröffentlichung prüfen' },
                { key: 'allowAnonymous', label: 'Anonyme Beiträge erlauben' },
                { key: 'emailNotifications', label: 'E-Mail-Benachrichtigungen bei neuen Beiträgen' },
              ].map((feature) => (
                <label key={feature.key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={features[feature.key as keyof typeof features] as boolean}
                    onChange={(e) => setFeatures(prev => ({ ...prev, [feature.key]: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-300 text-memorial-primary focus:ring-memorial-primary"
                  />
                  <span className="text-sm text-gray-700">{feature.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Privacy settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Datenschutz & Sichtbarkeit</h2>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Standard-Sichtbarkeit neuer Gedenkseiten
            </label>
            <select
              value={privacy.defaultVisibility}
              onChange={(e) => setPrivacy(prev => ({ ...prev, defaultVisibility: e.target.value }))}
              className="w-full max-w-xs px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-memorial-primary/20 focus:border-memorial-primary"
            >
              <option value="public">Öffentlich (jeder kann sie finden)</option>
              <option value="unlisted">Nicht gelistet (nur mit Link)</option>
              <option value="private">Privat (nur für Familie)</option>
            </select>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacy.showInSearch}
                onChange={(e) => setPrivacy(prev => ({ ...prev, showInSearch: e.target.checked }))}
                className="w-5 h-5 rounded border-gray-300 text-memorial-primary focus:ring-memorial-primary"
              />
              <span className="text-sm text-gray-700">Gedenkseiten in der Suche anzeigen</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacy.allowSharing}
                onChange={(e) => setPrivacy(prev => ({ ...prev, allowSharing: e.target.checked }))}
                className="w-5 h-5 rounded border-gray-300 text-memorial-primary focus:ring-memorial-primary"
              />
              <span className="text-sm text-gray-700">Teilen-Buttons auf Gedenkseiten anzeigen</span>
            </label>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-red-50 rounded-xl border border-red-200 p-6">
        <h2 className="text-lg font-medium text-red-900 mb-2">Gefahrenzone</h2>
        <p className="text-sm text-red-700 mb-4">
          Diese Aktionen können nicht rückgängig gemacht werden. Bitte seien Sie vorsichtig.
        </p>
        <div className="flex flex-wrap gap-3">
          <button className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm">
            Alle Daten exportieren
          </button>
          <button className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm">
            Portal zurücksetzen
          </button>
        </div>
      </div>
    </div>
  );
}
